'use client';
import { Box, BoxProps, Grid } from '@mui/material';
import { useCallback } from 'react';
import { MOTION, SHADOWS } from '../../constants';
import { Transition } from '../transition';

/**
 * Eases an element between its old and new height whenever its content changes, then hands the height back to the
 * layout. Content is measured before the browser paints, so the new height never flashes, and the element keeps its
 * normal auto height (and its children their percentage heights) whenever it is not animating.
 */
const useHeightEasing = () =>
  // A callback ref, because the card mounts after SafeSpace itself (inside the client-only Transition)
  useCallback((element: HTMLDivElement | null) => {
    if (!element || typeof MutationObserver === 'undefined' || typeof ResizeObserver === 'undefined') return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let lastHeight = element.offsetHeight;
    let animating = false;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    // Nested elements easing their own height (accordions, Collapse): the card follows them instead
    const nestedHeightTransitions = new Set<EventTarget>();

    const release = () => {
      clearTimeout(fallback);
      animating = false;
      Object.assign(element.style, { height: '', maxHeight: '', overflow: '', transition: '' });
      lastHeight = element.offsetHeight;
    };

    // Window resizes, fonts and images change the height without DOM mutations: just keep track of it
    const resizeObserver = new ResizeObserver(() => {
      if (!animating) lastHeight = element.offsetHeight;
    });

    const mutationObserver = new MutationObserver(() => {
      if (reduceMotion?.matches || nestedHeightTransitions.size) return;
      const from = animating ? element.getBoundingClientRect().height : lastHeight;
      Object.assign(element.style, { height: '', maxHeight: '', transition: 'none' });
      const to = element.offsetHeight;
      if (Math.abs(to - from) < 1) {
        if (animating) release();
        return;
      }
      animating = true;
      Object.assign(element.style, { height: `${from}px`, maxHeight: 'none', overflow: 'hidden' });
      void element.offsetHeight; // Commits the starting height so the change below transitions
      Object.assign(element.style, {
        height: `${to}px`,
        transition: `height ${MOTION.medium}ms ${MOTION.easeOut}`,
      });
      // transitionend never fires if the card is hidden or the transition gets interrupted
      clearTimeout(fallback);
      fallback = setTimeout(release, MOTION.medium + 100);
    });

    const onTransitionRun = (event: TransitionEvent) => {
      if (event.target === element || event.propertyName !== 'height' || !event.target) return;
      nestedHeightTransitions.add(event.target);
      // Hand the height back to the layout so the card grows in step with the nested animation
      if (animating) release();
    };

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'height') return;
      if (event.target === element) release();
      else if (event.target) nestedHeightTransitions.delete(event.target);
    };

    resizeObserver.observe(element);
    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });
    element.addEventListener('transitionrun', onTransitionRun);
    element.addEventListener('transitionend', onTransitionEnd);
    element.addEventListener('transitioncancel', onTransitionEnd);
    return () => {
      clearTimeout(fallback);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      element.removeEventListener('transitionrun', onTransitionRun);
      element.removeEventListener('transitionend', onTransitionEnd);
      element.removeEventListener('transitioncancel', onTransitionEnd);
    };
  }, []);

/**
 * Renders a safe space component with a maximum width of 540px on medium screens,
 * a background color of 'background.paper', and padding of 0 on extra small screens
 * and 8 on medium screens. The padding top is 4 on extra small screens. The border
 * radius is 6. The component is a section element. The rest of the props are spread
 * onto the Box component. The children are rendered inside the Box component.
 *
 * @param {BoxProps} props - The props for the SafeSpace component.
 * @param {ReactNode} props.children - The children to be rendered inside the Box component.
 * @returns {React.JSX.Element} The rendered SafeSpace component.
 */
export const SafeSpace = ({ children, ...rest }: BoxProps) => {
  // The card eases between steps, skeletons and error messages instead of jumping to its new height
  const cardRef = useHeightEasing();

  return (
    <Grid
      container
      sx={{
        justifyContent: { xs: 'center', md: 'end' },
        // Clip only on phones (full-bleed card, no shadow); from md the card's shadow must spill past this box
        overflow: { xs: 'hidden', md: 'visible' },
        maxWidth: '540px',
        minHeight: { xs: '70dvh', md: '630px' },
        width: { xs: '100vw', lg: '100%' },
      }}
      data-testid="safeSpace"
    >
      <Transition
        id="safeSpace_transition"
        type="fade"
        style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}
      >
        <Box
          ref={cardRef}
          component={'section'}
          sx={{
            minHeight: { md: '600px' },
            maxHeight: { md: 'fit-content' },
            width: { xs: '100%', md: '540px' },
            backgroundColor: 'background.paper',
            borderRadius: { xs: '0', md: '24px' },
            boxShadow: { xs: 'none', md: SHADOWS.card },
            ...rest.sx,
          }}
          {...rest}
        >
          {children}
        </Box>
      </Transition>
    </Grid>
  );
};
export const SafeSpacePadder = ({ children, ...rest }: BoxProps) => (
  <Box
    data-testid="safeSpacePadder"
    sx={{ width: '100%', padding: { xs: 0, md: 8 }, pt: { xs: 4 }, px: { xs: 3, md: 8 }, ...rest.sx }}
  >
    {children}
  </Box>
);
